// UltraFlex Admin — Finance Dashboard
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  PoundSterling, TrendingUp, TrendingDown, Building2, CreditCard,
  AlertTriangle, ArrowUpRight, ArrowDownRight, Calendar, BarChart3,
  RefreshCw, Users, Ticket, ShoppingBag, Clock, Download
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (val) => `£${(val || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatCard = ({ icon: Icon, label, value, subtext, color = 'text-uf-red', testId }) => (
  <div className="bg-uf-surface rounded-xl border border-uf-border p-5" data-testid={testId}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-opacity-15 ${color.replace('text-', 'bg-')}/15`}>
        <Icon className={`w-4.5 h-4.5 ${color}`} />
      </div>
      <span className="text-uf-text-muted text-xs">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    {subtext && <p className="text-uf-text-muted text-xs mt-1">{subtext}</p>}
  </div>
);

const AdminFinanceDashboard = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [period, setPeriod] = useState('month');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${API_URL}/api/sites`, { headers }).then(r => r.json())
      .then(d => setSites(d.items || [])).catch(() => {});
  }, []);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/finance-dashboard?period=${period}`;
      if (selectedSite) url += `&site_id=${selectedSite}`;
      const res = await fetch(url, { headers });
      setData(await res.json());
    } catch { setData(null); }
    setLoading(false);
  }, [selectedSite, period, token]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const rev = data?.revenue || {};
  const payments = data?.payments || {};
  const trend = data?.monthly_trend || [];
  const siteBreakdown = data?.site_breakdown || [];

  // Calculate trend bar chart max
  const maxTrend = Math.max(...trend.map(t => t.total), 1);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-finance-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-uf-text" data-testid="finance-page-title">Finance Dashboard</h1>
          <p className="text-uf-text-muted text-sm mt-1">
            {data?.period?.from && `${data.period.from} to ${data.period.to}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select value={period} onChange={e => setPeriod(e.target.value)} data-testid="finance-period"
            className="bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
          <select value={selectedSite} onChange={e => setSelectedSite(e.target.value)} data-testid="finance-site-filter"
            className="bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
            <option value="">All Sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button onClick={fetchDashboard} className="p-2 text-uf-text-muted hover:text-uf-text" data-testid="finance-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          <Link to="/admin/finance" className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm hover:bg-uf-red-hover" data-testid="export-reports-link">
            <Download className="w-4 h-4" />Export Reports
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="bg-uf-surface rounded-xl border border-uf-border p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto" />
        </div>
      ) : (
        <>
          {/* Revenue Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={PoundSterling} label="Total Revenue" value={formatCurrency(rev.total)} color="text-uf-red" testId="stat-total-rev" />
            <StatCard icon={CreditCard} label="Memberships" value={formatCurrency(rev.memberships)} color="text-blue-400" testId="stat-membership-rev" />
            <StatCard icon={Ticket} label="Day Passes" value={formatCurrency(rev.day_passes)} color="text-emerald-400" testId="stat-daypass-rev" />
            <StatCard icon={ShoppingBag} label="Shop Sales" value={formatCurrency(rev.shop)} color="text-orange-400" testId="stat-shop-rev" />
          </div>

          {/* Revenue Pie Chart */}
          {rev.total > 0 && (() => {
            const total = rev.total || 1;
            const memPct = ((rev.memberships || 0) / total) * 100;
            const dayPct = ((rev.day_passes || 0) / total) * 100;
            const shopPct = ((rev.shop || 0) / total) * 100;
            const otherPct = Math.max(0, 100 - memPct - dayPct - shopPct);
            return (
              <div className="bg-uf-surface rounded-xl border border-uf-border p-5" data-testid="revenue-pie-chart">
                <h3 className="text-sm font-semibold text-uf-text mb-4">Revenue Distribution</h3>
                <div className="flex items-center gap-8">
                  <div className="relative w-40 h-40 flex-shrink-0">
                    <svg viewBox="0 0 42 42" className="w-full h-full -rotate-90">
                      <circle cx="21" cy="21" r="15.91549430918954" fill="transparent" stroke="#3b82f6" strokeWidth="6"
                        strokeDasharray={`${memPct} ${100 - memPct}`} strokeDashoffset="0" />
                      <circle cx="21" cy="21" r="15.91549430918954" fill="transparent" stroke="#22c55e" strokeWidth="6"
                        strokeDasharray={`${dayPct} ${100 - dayPct}`} strokeDashoffset={`${-memPct}`} />
                      <circle cx="21" cy="21" r="15.91549430918954" fill="transparent" stroke="#f97316" strokeWidth="6"
                        strokeDasharray={`${shopPct} ${100 - shopPct}`} strokeDashoffset={`${-(memPct + dayPct)}`} />
                      {otherPct > 0 && (
                        <circle cx="21" cy="21" r="15.91549430918954" fill="transparent" stroke="#64748b" strokeWidth="6"
                          strokeDasharray={`${otherPct} ${100 - otherPct}`} strokeDashoffset={`${-(memPct + dayPct + shopPct)}`} />
                      )}
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-lg font-bold text-uf-text">{formatCurrency(rev.total)}</p>
                        <p className="text-[10px] text-uf-text-muted">Total</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3 flex-1">
                    {[
                      { label: 'Memberships', value: rev.memberships, pct: memPct, color: 'bg-blue-500' },
                      { label: 'Day Passes', value: rev.day_passes, pct: dayPct, color: 'bg-emerald-500' },
                      { label: 'Shop Sales', value: rev.shop, pct: shopPct, color: 'bg-orange-500' },
                    ].map(item => (
                      <div key={item.label} className="flex items-center gap-3">
                        <span className={`w-3 h-3 rounded-full ${item.color} flex-shrink-0`} />
                        <div className="flex-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-uf-text">{item.label}</span>
                            <span className="text-uf-text-muted">{item.pct.toFixed(1)}%</span>
                          </div>
                          <div className="w-full h-1.5 bg-uf-surface-hi rounded-full mt-1">
                            <div className={`h-full rounded-full ${item.color}`} style={{ width: `${item.pct}%` }} />
                          </div>
                        </div>
                        <span className="text-uf-text font-medium text-sm w-20 text-right">{formatCurrency(item.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Payment Status */}
          <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
            <h3 className="text-sm font-semibold text-uf-text mb-4">Payment Status Breakdown</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { key: 'collected', label: 'Collected', color: 'text-emerald-400 bg-emerald-500/15', icon: ArrowUpRight },
                { key: 'pending', label: 'Pending', color: 'text-yellow-400 bg-yellow-500/15', icon: Clock },
                { key: 'failed', label: 'Failed', color: 'text-red-400 bg-red-500/15', icon: AlertTriangle },
                { key: 'refunded', label: 'Refunded', color: 'text-blue-400 bg-blue-500/15', icon: ArrowDownRight },
              ].map(({ key, label, color, icon: Icon }) => {
                const stat = payments[key] || { count: 0, amount: 0 };
                return (
                  <div key={key} className={`rounded-lg p-4 ${color.split(' ')[1]}`} data-testid={`payment-${key}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`w-4 h-4 ${color.split(' ')[0]}`} />
                      <span className={`text-xs font-medium ${color.split(' ')[0]}`}>{label}</span>
                    </div>
                    <p className={`text-xl font-bold ${color.split(' ')[0]}`}>{formatCurrency(stat.amount)}</p>
                    <p className="text-uf-text-muted text-xs mt-1">{stat.count} payment{stat.count !== 1 ? 's' : ''}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Monthly Trend */}
          <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
            <h3 className="text-sm font-semibold text-uf-text mb-4">6 Month Revenue Trend</h3>
            {trend.length > 0 ? (
              <div className="flex items-end gap-3 h-48">
                {trend.map((t, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1" data-testid={`trend-${i}`}>
                    <span className="text-uf-text-muted text-[10px]">{formatCurrency(t.total)}</span>
                    <div className="w-full flex flex-col gap-0.5">
                      <div className="bg-blue-500/60 rounded-t" style={{ height: `${Math.max((t.memberships / maxTrend) * 140, 2)}px` }} />
                      <div className="bg-emerald-500/60" style={{ height: `${Math.max((t.day_passes / maxTrend) * 140, 1)}px` }} />
                    </div>
                    <span className="text-uf-text-muted text-[10px]">{t.month}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-uf-text-muted text-sm text-center py-8">No trend data available</p>
            )}
            <div className="flex items-center gap-4 mt-3 text-xs text-uf-text-muted">
              <span className="flex items-center gap-1"><span className="w-3 h-2 bg-blue-500/60 rounded" /> Memberships</span>
              <span className="flex items-center gap-1"><span className="w-3 h-2 bg-emerald-500/60 rounded" /> Day Passes</span>
            </div>
          </div>

          {/* Site Breakdown (only when viewing all sites) */}
          {siteBreakdown && siteBreakdown.length > 0 && (
            <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
              <h3 className="text-sm font-semibold text-uf-text mb-4">Revenue by Site</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="site-breakdown-table">
                  <thead>
                    <tr className="border-b border-uf-border">
                      <th className="text-left py-2 text-uf-text-muted font-medium text-xs">Site</th>
                      <th className="text-right py-2 text-uf-text-muted font-medium text-xs">Memberships</th>
                      <th className="text-right py-2 text-uf-text-muted font-medium text-xs">Day Passes</th>
                      <th className="text-right py-2 text-uf-text-muted font-medium text-xs">Total</th>
                      <th className="text-right py-2 text-uf-text-muted font-medium text-xs">Active Members</th>
                    </tr>
                  </thead>
                  <tbody>
                    {siteBreakdown.map(site => (
                      <tr key={site.site_id} className="border-b border-uf-border/30" data-testid={`site-row-${site.site_id}`}>
                        <td className="py-3 text-uf-text font-medium">{site.site_name}</td>
                        <td className="py-3 text-right text-uf-text">{formatCurrency(site.membership_revenue)}</td>
                        <td className="py-3 text-right text-uf-text">{formatCurrency(site.day_pass_revenue)}</td>
                        <td className="py-3 text-right text-uf-red font-semibold">{formatCurrency(site.total_revenue)}</td>
                        <td className="py-3 text-right text-uf-text">{site.active_members}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminFinanceDashboard;
